<template>
    <BaseModal
        :isVisible="modalVisualizarLog"
        :title="`Detalhes do Log de Integração #${selectedLog ? selectedLog.id : 0}`"
        size="xl"
        @close="$emit('close')"
    >
        <template v-slot:content>
            <div v-if="selectedLog">

                <!-- Sistema de Abas -->
                <div class="tabs-container mb-4">
                    <ul
                        class="nav nav-tabs"
                        role="tablist"
                    >
                        <li class="nav-item">
                            <a
                                class="nav-link"
                                :class="{ active: activeTab === 'log' }"
                                @click="updateTab('log')"
                                href="#"
                                role="tab"
                            >
                                <i class="fas fa-file-alt mr-2"></i>
                                Dados do Log
                            </a>
                        </li>
                        <li
                            class="nav-item"
                            v-if="selectedLog.reserve_id"
                        >
                            <a
                                class="nav-link"
                                :class="{ active: activeTab === 'reservation' }"
                                @click="loadReservationData"
                                href="#"
                                role="tab"
                            >
                                <i class="fas fa-calendar-check mr-2"></i>
                                Dados da Reserva
                                <i
                                    v-if="loadingReservation"
                                    class="fas fa-spinner fa-spin ml-2"
                                ></i>
                            </a>
                        </li>
                    </ul>
                </div>

                <!-- Conteúdo da Aba do Log -->
                <div v-show="activeTab === 'log'">
                    <!-- Dados da Integração -->
                    <SectionContainer
                        title="Informações do Log"
                        icon="exchange-alt"
                    >
                        <div class="integration-details">
                            <div class="detail-row">
                                <div class="detail-label">
                                    <i class="fas fa-hashtag"></i>
                                    ID do Log:
                                </div>
                                <div class="detail-value">
                                    <strong>{{ selectedLog.id }}</strong>
                                </div>
                            </div>

                            <div
                                class="detail-row"
                                v-if="selectedLog.reserve_id"
                            >
                                <div class="detail-label">
                                    <i class="fas fa-calendar"></i>
                                    ID da Reserva:
                                </div>
                                <div class="detail-value">
                                    <strong class="text-primary">{{ selectedLog.reserve_id }}</strong>
                                </div>
                            </div>

                            <div class="detail-row">
                                <div class="detail-label">
                                    <i class="fas fa-cog"></i>
                                    Serviço:
                                </div>
                                <div class="detail-value">
                                    {{ selectedLog.service_name || 'CopacolService' }}
                                </div>
                            </div>

                            <div class="detail-row">
                                <div class="detail-label">
                                    <i class="fas fa-code"></i>
                                    Método:
                                </div>
                                <div class="detail-value">
                                    {{ getMethodLabel(selectedLog.method_name) }}
                                </div>
                            </div>

                            <div class="detail-row">
                                <div class="detail-label">
                                    <i class="fas fa-info-circle"></i>
                                    Status:
                                </div>
                                <div class="detail-value">
                                    <span
                                        class="status-badge"
                                        :class="getIntegrationStatusClass(selectedLog.status)"
                                    >
                                        {{ getStatusLabel(selectedLog.status) }}
                                    </span>
                                </div>
                            </div>

                            <div
                                class="detail-row"
                                v-if="selectedLog.error_type"
                            >
                                <div class="detail-label">
                                    <i class="fas fa-exclamation-triangle"></i>
                                    Tipo de Erro:
                                </div>
                                <div class="detail-value">
                                    <span
                                        class="error-badge"
                                        :class="getErrorTypeClass(selectedLog.error_type)"
                                    >
                                        {{ getErrorTypeLabel(selectedLog.error_type) }}
                                    </span>
                                </div>
                            </div>

                            <div
                                class="detail-row"
                                v-if="selectedLog.http_status_code"
                            >
                                <div class="detail-label">
                                    <i class="fas fa-globe"></i>
                                    Código HTTP:
                                </div>
                                <div class="detail-value">
                                    <span :class="getHttpStatusClass(selectedLog.http_status_code)">
                                        {{ selectedLog.http_status_code }}
                                    </span>
                                </div>
                            </div>

                            <div class="detail-row">
                                <div class="detail-label">
                                    <i class="fas fa-stopwatch"></i>
                                    Tempo de Execução:
                                </div>
                                <div class="detail-value">
                                    {{ formatExecutionTime(selectedLog.execution_time) }}
                                </div>
                            </div>

                            <div class="detail-row">
                                <div class="detail-label">
                                    <i class="fas fa-calendar"></i>
                                    Data/Hora:
                                </div>
                                <div class="detail-value date-highlight">
                                    {{ formatDateTime(selectedLog.created_at) }}
                                </div>
                            </div>

                            <!-- Mensagem de erro se houver -->
                            <div
                                class="detail-row"
                                v-if="selectedLog.error_message"
                            >
                                <div class="detail-label">
                                    <i class="fas fa-exclamation-circle"></i>
                                    Mensagem de Erro:
                                </div>
                                <div class="detail-value">
                                    <div class="error-message">
                                        {{ selectedLog.error_message }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SectionContainer>

                    <!-- Dados da Requisição e Resposta -->
                    <SectionContainer
                        title="Dados Técnicos"
                        icon="code"
                    >
                        <div class="technical-data">
                            <!-- Dados da Requisição -->
                            <div class="data-section mt-3">
                                <h6>
                                    <i class="fas fa-arrow-up mr-2"></i>
                                    Dados da Requisição
                                    <button
                                        class="btn btn-sm btn-outline-primary float-right"
                                        @click="copyToClipboard(selectedLog.request_data, 'request')"
                                    >
                                        {{ currentCopiedStates.request ? 'Copiado!' : 'Copiar JSON' }}
                                    </button>
                                </h6>
                                <pre class="json-data bg-dark">{{ formatJson(selectedLog.request_data) }}</pre>
                            </div>

                            <!-- Dados da Resposta -->
                            <div
                                class="data-section"
                                v-if="selectedLog.response_data"
                            >
                                <h6>
                                    <i class="fas fa-arrow-down mr-2"></i>
                                    Dados da Resposta
                                    <button
                                        class="btn btn-sm btn-outline-primary float-right"
                                        @click="copyToClipboard(selectedLog.response_data, 'response')"
                                    >
                                        {{ currentCopiedStates.response ? 'Copiado!' : 'Copiar JSON' }}
                                    </button>
                                </h6>
                                <pre class="json-data bg-dark">{{ formatJson(selectedLog.response_data) }}</pre>
                            </div>
                        </div>
                    </SectionContainer>
                </div>

                <!-- Conteúdo da Aba da Reserva -->
                <div v-show="activeTab === 'reservation'">
                    <div
                        v-if="loadingReservation"
                        class="text-center py-4"
                    >
                        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
                        <p class="mt-2">Carregando dados da reserva...</p>
                    </div>

                    <div v-else-if="reservationDetails">
                        <!-- Dados da Reserva -->
                        <SectionContainer
                            title="Informações da Reserva"
                            icon="calendar-check"
                        >
                            <div class="reservation-details">
                                <div class="detail-row">
                                    <div class="detail-label">
                                        <i class="fas fa-hashtag"></i>
                                        ID da Reserva:
                                    </div>
                                    <div class="detail-value">
                                        <strong>{{ reservationDetails.id }}</strong>
                                    </div>
                                </div>

                                <div class="detail-row">
                                    <div class="detail-label">
                                        <i class="fas fa-user"></i>
                                        Usuário:
                                    </div>
                                    <div class="detail-value">
                                        {{ reservationDetails.user ? reservationDetails.user.name : 'N/A' }}
                                    </div>
                                </div>

                                <div class="detail-row">
                                    <div class="detail-label">
                                        <i class="fas fa-building"></i>
                                        Centro de Custo:
                                    </div>
                                    <div class="detail-value">
                                        {{
                                            reservationDetails.cost_center ? reservationDetails.cost_center.description : 'N/A'
                                        }}
                                    </div>
                                </div>

                                <div class="detail-row">
                                    <div class="detail-label">
                                        <i class="fas fa-car"></i>
                                        Veículo:
                                    </div>
                                    <div class="detail-value">
                                        {{
                                            reservationDetails.vehicles ? reservationDetails.vehicles.description : 'N/A'
                                        }}
                                    </div>
                                </div>

                                <div class="detail-row">
                                    <div class="detail-label">
                                        <i class="fas fa-hashtag"></i>
                                        Placa:
                                    </div>
                                    <div class="detail-value">
                                        {{ reservationDetails.vehicles ? reservationDetails.vehicles.board : 'N/A' }}
                                    </div>
                                </div>

                                <div class="detail-row">
                                    <div class="detail-label">
                                        <i class="fas fa-clock"></i>
                                        Horário Retirada:
                                    </div>
                                    <div class="detail-value date-highlight">
                                        {{ reservationDetails.retreat_date }}
                                    </div>
                                </div>

                                <div class="detail-row">
                                    <div class="detail-label">
                                        <i class="fas fa-clock"></i>
                                        Horário Devolução:
                                    </div>
                                    <div class="detail-value date-highlight">
                                        {{ reservationDetails.devolution_date }}
                                    </div>
                                </div>

                                <div
                                    class="detail-row"
                                    v-if="reservationDetails.observations"
                                >
                                    <div class="detail-label">
                                        <i class="fas fa-sticky-note"></i>
                                        Observações:
                                    </div>
                                    <div class="detail-value">
                                        {{ reservationDetails.observations }}
                                    </div>
                                </div>

                                <div class="detail-row">
                                    <div class="detail-label">
                                        <i class="fas fa-info-circle"></i>
                                        Status:
                                    </div>
                                    <div class="detail-value">
                                        <span
                                            class="status-badge"
                                            :class="getReservationStatusClass(reservationDetails.reserve_status ? reservationDetails.reserve_status.id : 0)"
                                        >
                                            {{
                                                reservationDetails.reserve_status ? reservationDetails.reserve_status.title : 'N/A'
                                            }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </SectionContainer>
                    </div>

                    <div
                        v-else
                        class="text-center py-4"
                    >
                        <i class="fas fa-exclamation-triangle fa-2x text-warning"></i>
                        <p class="mt-2">Não foi possível carregar os dados da reserva</p>
                    </div>
                </div>

                <!-- Botões de Ação -->
                <div class="modal-actions mt-4">
                    <!-- Botão de retry apenas para logs com erro -->
                    <button
                        class="btn btn-primary"
                        @click="$emit('retry-integration')"
                        v-if="selectedLog && selectedLog.status !== 'success' && canRetry(selectedLog.method_name)"
                        :disabled="retryLoading"
                    >
                        <i
                            class="fas fa-redo mr-2"
                            :class="{ 'fa-spin': retryLoading }"
                        ></i>
                        {{ retryLoading ? 'Tentando...' : 'Tentar Novamente' }}
                    </button>
                </div>
            </div>
            <div v-else class="text-center py-4">
                <i class="fas fa-exclamation-triangle fa-2x text-warning"></i>
                <p class="mt-2">Nenhum log selecionado</p>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import SectionContainer from "../Modals/Checklist/SectionContainer.vue" // container de seção
import BaseModal from "../Base/BaseModal.vue" // modal base
import {
    getHttpStatusClass, // classe CSS para status HTTP
    getIntegrationStatusClass, // classe CSS para status de integração
    getMethodLabel, // label para método
    getReservationStatusClass, // classe CSS para status de reserva
    getStatusLabel, // label para status
    getErrorTypeClass, // classe CSS para tipo de erro
    getErrorTypeLabel // label para tipo de erro
} from "../../composables/mask"

export default {
    name: 'ModalRequest', // nome do componente

    components: {
        BaseModal, // modal base
        SectionContainer // container de seção
    },

    props: {
        modalVisualizarLog: {
            type: Boolean,
            required: true,
            default: false
        },
        selectedLog: {
            type: Object,
            default: null
        },
        reservationDetails: {
            type: Object,
            default: null
        },
        loadingReservation: {
            type: Boolean,
            default: false
        },
        copiedStates: {
            type: Object,
            default: () => ({
                request: false,
                response: false
            })
        },
        activeTab: {
            type: String,
            default: 'log'
        },
        retryLoading: {
            type: Boolean,
            default: false
        },
        loadingStats: {
            type: Boolean,
            required: true
        },
        statsData: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            // Estados de cópia internos / Internal copy states
            internalCopiedStates: {
                request: false, // estado de cópia da requisição
                response: false // estado de cópia da resposta
            }
        }
    },

    computed: {
        /**
         * Estado de cópia combinado (props + interno) / Combined copy state (props + internal)
         */
        currentCopiedStates() {
            return {
                request: this.copiedStates.request || this.internalCopiedStates.request,
                response: this.copiedStates.response || this.internalCopiedStates.response
            }
        }
    },

    watch: {
        /**
         * Observa mudanças no modal para resetar estados / Watch modal changes to reset states
         */
        modalVisualizarLog(newValue) {
            if (!newValue) {
                // Reset estados quando fechar o modal / Reset states when closing modal
                this.internalCopiedStates.request = false
                this.internalCopiedStates.response = false
            }
        },

        /**
         * Observa mudanças no log selecionado / Watch changes in selected log
         */
        selectedLog(newValue) {
            console.log('Log selecionado mudou para:', newValue); // debug
        },

        /**
         * Observa mudanças na aba ativa / Watch changes in active tab
         */
        activeTab(newValue) {
            console.log('Aba ativa mudou para:', newValue); // debug
        }
    },

    methods: {
        // Métodos importados / Imported methods
        getMethodLabel,
        getIntegrationStatusClass,
        getStatusLabel,
        getHttpStatusClass,
        getReservationStatusClass,
        getErrorTypeClass,
        getErrorTypeLabel,

        /**
         * Atualiza a aba ativa / Update active tab
         */
        updateTab(tab) {
            this.$emit('update-active-tab', tab)
        },

        /**
         * Carrega dados da reserva / Load reservation data
         */
        loadReservationData() {
            this.$emit('update-active-tab', 'reservation')
            this.$emit('load-reservation')
        },

        /**
         * Formata tempo de execução / Format execution time
         */
        formatExecutionTime(time) {
            if (!time && time !== 0) return '-'
            if (time < 1000) {
                return Math.round(time) + 'ms'
            }
            return (time / 1000).toFixed(2) + 's'
        },

        /**
         * Formata data e hora / Format date and time
         */
        formatDateTime(date) {
            if (!date) return '-'
            const d = new Date(date)
            return d.toLocaleString('pt-BR')
        },

        /**
         * Formata JSON para exibição / Format JSON for display
         */
        formatJson(data) {
            if (!data) return 'N/A'
            try {
                return JSON.stringify(data, null, 2)
            } catch (e) {
                return String(data)
            }
        },

        /**
         * Copia conteúdo para clipboard / Copy content to clipboard
         */
        async copyToClipboard(data, type) {
            try {
                const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2)

                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(text)
                } else {
                    // Fallback para navegadores mais antigos / Fallback for older browsers
                    const textArea = document.createElement('textarea')
                    textArea.value = text
                    textArea.style.position = 'fixed'
                    textArea.style.left = '-999999px'
                    textArea.style.top = '-999999px'
                    document.body.appendChild(textArea)
                    textArea.focus()
                    textArea.select()
                    document.execCommand('copy')
                    textArea.remove()
                }

                // Atualiza estado temporariamente / Update state temporarily
                this.internalCopiedStates[type] = true

                // Emite evento para o componente pai / Emit event to parent component
                this.$emit('copy-to-clipboard', data, type)

                setTimeout(() => {
                    this.internalCopiedStates[type] = false
                }, 2000)

            } catch (err) {
                console.error('Erro ao copiar:', err)
                // Fallback manual para casos extremos / Manual fallback for extreme cases
                const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2)
                window.prompt('Copie o texto abaixo:', text)
            }
        },

        /**
         * Verifica se pode retentar a integração / Check if integration can be retried
         */
        canRetry(method) {
            // Métodos que permitem retry / Methods that allow retry
            const retryableMethods = ['setReserve', 'updateReserve', 'authenticate']
            return retryableMethods.includes(method)
        }
    },
}
</script>

<style scoped>
/* Detalhes do modal / Modal details */
.detail-row {
    display: flex;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #e3e6f0;
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    min-width: 180px;
    font-weight: 600;
    color: #5a5c69;
    display: flex;
    align-items: center;
}

.detail-label i {
    margin-right: 0.5rem;
    width: 16px;
    text-align: center;
}

.detail-value {
    flex: 1;
    color: #3a3b45;
}

.date-highlight {
    color: #2e59d9 !important;
    font-weight: 600;
}

/* Status badges */
.status-badge, .error-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    font-weight: 600;
}

.status-success { background-color: #1cc88a; color: white; }
.status-error { background-color: #e74a3b; color: white; }
.status-timeout { background-color: #f6c23e; color: #212529; }
.status-processing { background-color: #36b9cc; color: white; }
.status-default { background-color: #858796; color: white; }

.status-pre-reserva { background-color: #f8f9fc; color: #5a5c69; border: 1px solid #d1d3e2; }
.status-reservado { background-color: #1cc88a; color: white; }
.status-cancelado { background-color: #e74a3b; color: white; }
.status-retirado { background-color: #36b9cc; color: white; }
.status-devolvido { background-color: #858796; color: white; }

/* Mensagem de erro / Error message */
.error-message {
    background-color: #f8d7da;
    color: #721c24;
    padding: 0.75rem;
    border-radius: 0.25rem;
    border: 1px solid #f5c6cb;
}

/* Dados técnicos / Technical data */
.technical-data .data-section {
    margin-bottom: 2rem;
}

.json-data {
    background-color: #f8f9fc;
    border: 1px solid #d1d3e2;
    border-radius: 0.25rem;
    padding: 1rem;
    font-size: 0.875rem;
    max-height: 300px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
}

/* Tabs */
.nav-tabs .nav-link {
    border: 1px solid transparent;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
    color: #858796;
    cursor: pointer;
}

.nav-tabs .nav-link.active {
    color: #2e59d9;
    background-color: #fff;
    border-color: #d1d3e2 #d1d3e2 #fff;
}

.nav-tabs .nav-link:hover {
    border-color: #e3e6f0 #e3e6f0 #d1d3e2;
    color: #2e59d9;
}

/* Actions */
.modal-actions {
    border-top: 1px solid #e3e6f0;
    padding-top: 1rem;
}

/* Responsividade / Responsiveness */
@media (max-width: 768px) {
    .detail-row {
        flex-direction: column;
        align-items: flex-start;
        padding: 1rem 0;
    }

    .detail-label {
        min-width: auto;
        margin-bottom: 0.5rem;
    }

    .detail-value {
        width: 100%;
    }

    .json-data {
        font-size: 0.75rem;
        max-height: 200px;
    }
}

/* Transições / Transitions */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/IntegrationLog/ModalRequest.vue